import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { DatabaseCounts } from '../types/api.types';

/**
 * Hook to fetch counts for all database data types (articles, sales, stock, etc.)
 * Used to display counts in DatabaseManage tabs
 */
const defaultCounts: DatabaseCounts = {
  articles: null,
  sales: null,
  lifecycles: null,
  stock: null,
  suppliers: null,
  articleSuppliers: null,
};

export function useDatabaseCounts(
  databaseId: number | string | null
) {
  const queryResult = useQuery({
    queryKey: ['databaseCounts', databaseId],
    queryFn: async (): Promise<DatabaseCounts> => {
      if (!databaseId) {
        return defaultCounts;
      }

      // Fetch all counts in parallel
      const [
        articlesResponse,
        salesResponse,
        lifecyclesResponse,
        stockResponse,
        suppliersResponse,
        articleSuppliersResponse,
      ] = await Promise.allSettled([
        api.get(`${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.ARTICLES_COUNT(databaseId)}`),
        api.get(`${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.SALES_COUNT(databaseId)}`),
        api.get(`${API_ROUTES.LIFE_CYCLE.BASE}${API_ROUTES.LIFE_CYCLE.COUNT(String(databaseId))}`),
        api.get(`${API_ROUTES.STOCK.BASE}${API_ROUTES.STOCK.COUNT(String(databaseId))}`),
        api.get(`${API_ROUTES.SUPPLIERS.BASE}${API_ROUTES.SUPPLIERS.COUNT(String(databaseId))}`),
        api.get(`${API_ROUTES.ARTICLE_SUPPLIERS.BASE}${API_ROUTES.ARTICLE_SUPPLIERS.COUNT(String(databaseId))}`),
      ]);

      // Extract counts from responses, handling failures gracefully
      const getCount = (result: PromiseSettledResult<any>): number | null => {
        if (result.status === 'fulfilled' && result.value?.data?.count !== undefined) {
          return result.value.data.count;
        }
        return null;
      };

      return {
        articles: getCount(articlesResponse),
        sales: getCount(salesResponse),
        lifecycles: getCount(lifecyclesResponse),
        stock: getCount(stockResponse),
        suppliers: getCount(suppliersResponse),
        articleSuppliers: getCount(articleSuppliersResponse),
      };
    },
    enabled: !!databaseId,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    retry: false, // Don't retry failed requests - we handle nulls gracefully
  });

  // Return default counts when query is disabled (but not when loading)
  return {
    ...queryResult,
    data: queryResult.data ?? (queryResult.isLoading ? undefined : defaultCounts),
  };
}

